export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface CommandButtonSpec {
  readonly id: string;
  readonly width?: number;
  readonly height?: number;
}

export interface CommandButtonPlacement extends Rect {
  readonly id: string;
}

export interface CommandButtonLayoutSpec {
  readonly contentWidth: number;
  readonly defaultWidth: number;
  readonly defaultHeight: number;
  readonly gapX: number;
  readonly gapY: number;
  readonly startY?: number;
}

export interface CommandButtonLayoutResult {
  readonly placements: readonly CommandButtonPlacement[];
  readonly totalHeight: number;
}

export interface PopoverPositionSpec {
  readonly anchor: Rect;
  readonly popover: { readonly width: number; readonly height: number };
  readonly viewport: { readonly width: number; readonly height: number };
  readonly margin?: number;
}

export interface DropdownListYSpec {
  readonly controlY: number;
  readonly controlHeight: number;
  readonly dropdownHeight: number;
  readonly popoverY: number;
  readonly viewportHeight: number;
  readonly margin?: number;
}

export function layoutCommandButtons(
  commands: readonly CommandButtonSpec[],
  spec: CommandButtonLayoutSpec,
): CommandButtonLayoutResult {
  const startY = spec.startY ?? 0;
  let x = 0;
  let y = startY;
  let rowH = 0;
  const placements: CommandButtonPlacement[] = [];

  for (const command of commands) {
    const width = command.width ?? spec.defaultWidth;
    const height = command.height ?? spec.defaultHeight;
    if (x > 0 && x + width > spec.contentWidth) {
      x = 0;
      y += rowH + spec.gapY;
      rowH = 0;
    }
    placements.push({ id: command.id, x, y, width, height });
    x += width + spec.gapX;
    rowH = Math.max(rowH, height);
  }

  return {
    placements,
    totalHeight: placements.length === 0 ? 0 : y - startY + rowH,
  };
}

export function computePopoverPosition(spec: PopoverPositionSpec): { readonly x: number; readonly y: number } {
  const margin = spec.margin ?? 8;
  let x = spec.anchor.x + spec.anchor.width;
  let y = spec.anchor.y;

  if (x + spec.popover.width > spec.viewport.width - margin) {
    x = spec.anchor.x - spec.popover.width;
  }
  if (x < margin) {
    x = Math.max(margin, (spec.viewport.width - spec.popover.width) / 2);
  }

  if (y + spec.popover.height > spec.viewport.height - margin) {
    y = spec.anchor.y + spec.anchor.height - spec.popover.height;
  }
  if (y < margin) y = margin;

  return { x, y };
}

export function computeDropdownListY(spec: DropdownListYSpec): number {
  const margin = spec.margin ?? 8;
  const belowY = spec.controlY + spec.controlHeight / 2 + 4;
  const aboveY = spec.controlY - spec.controlHeight / 2 - 4 - spec.dropdownHeight;
  let y = belowY;

  if (spec.popoverY + y + spec.dropdownHeight > spec.viewportHeight - margin) {
    y = aboveY;
  }
  if (spec.popoverY + y < margin) {
    y = margin - spec.popoverY;
  }

  return y;
}
